/**
 * Just Carry On — storefront behaviour.
 *
 * Everything here is an enhancement: content, navigation and forms work without it.
 * Header (transparent over the hero, category dropdowns, phone drawer), the hero
 * slideshow, search sheet with predictive results and search intents, section reveals,
 * product-card tilt and quick view, detail tabs, the size-guide dialog, the phone dock,
 * generic quantity steppers and the Instagram feed.
 */
(function () {
  'use strict';

  window.__jcoReady = true;

  var JCO = (window.JustCarryOn = window.JustCarryOn || {});
  var strings = JCO.strings || {};
  var root = document.documentElement;
  var body = document.body;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var desktop = window.matchMedia('(min-width: 990px)');
  var phone = window.matchMedia('(max-width: 749px)');

  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  function fill(template, values) {
    return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  /* ------------------------------------------------------------------ *
   * Prices. The store's money format may wrap every amount in markup for a currency
   * converter app, which converts the page once it loads. Prices swapped in later —
   * another variant, the quick view, search suggestions, more products, the cart — ask it
   * to convert again, and a `jco:prices-changed` event tells any other app.
   * ------------------------------------------------------------------ */
  JCO.pricesChanged = function (scope) {
    var bucks = window.bucksCC;
    if (bucks && typeof bucks.reConvert === 'function') {
      try { bucks.reConvert(); } catch (err) { /* the converter's own problem; prices stay as Shopify wrote them */ }
    }
    document.dispatchEvent(new CustomEvent('jco:prices-changed', { detail: { scope: scope || document } }));
  };

  /* ------------------------------------------------------------------ *
   * Search intents — the rules in Theme settings → Search → Search intents, written
   * "Label: word, word, … = link". JCO.matchIntent(query) returns the rule whose longest
   * word appears in the query (so "top handle bag" beats "bag"), with `exact` set when the
   * whole query is that word. "under 2000" (also "below", "less than", "up to", "within")
   * reads as a price: alone it points to every product up to that price, beside a word it
   * narrows that word's collection. snippets/search-intent.liquid reads the same way.
   * ------------------------------------------------------------------ */
  var normaliseQuery = function (text) {
    return String(text || '').toLowerCase()
      .replace(/₹|rs\.|\brs\b|\binr\b|\/-/g, ' ')
      .replace(/(\d),(?=\d)/g, '$1')
      .replace(/[–—\-,]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };
  var INTENTS = String(JCO.searchIntents || '').split(/\r?\n/).map(function (line) {
    var eq = line.indexOf(' = ');
    if (eq < 0) return null;
    var head = line.slice(0, eq);
    var colon = head.indexOf(':');
    if (colon < 0) return null;
    var words = head.slice(colon + 1).split(',').map(normaliseQuery).filter(Boolean);
    var rule = { label: head.slice(0, colon).trim(), url: line.slice(eq + 3).trim(), words: words };
    return rule.label && rule.url && words.length ? rule : null;
  }).filter(Boolean);
  var PRICE = /\b(?:under|below|less than|up to|upto|within)\s+(\d{2,7})\b/;

  /** A whole amount in the store's money format, as text: 2000 → "₹2,000". */
  var formatWhole = function (amount) {
    var grouped = String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return String(JCO.moneyFormat || '{{amount_no_decimals}}')
      .replace(/\{\{\s*\w+\s*\}\}/, grouped)
      .replace(/<[^>]*>/g, '')
      .trim();
  };

  JCO.matchIntent = function (query) {
    var q = normaliseQuery(query);
    if (!q) return null;
    var price = q.match(PRICE);
    var rest = price ? q.replace(price[0], ' ').replace(/\s+/g, ' ').trim() : q;
    var best = null;
    if (rest) {
      var padded = ' ' + rest + ' ';
      INTENTS.forEach(function (rule) {
        rule.words.forEach(function (word) {
          if (padded.indexOf(' ' + word + ' ') < 0 || (best && word.length <= best.size)) return;
          best = { label: rule.label, url: rule.url, size: word.length, exact: rest === word };
        });
      });
    }
    if (!price) return best;
    // A rule that already carries a filter of its own keeps it.
    if (best && best.url.indexOf('?') > -1) return best;
    var amount = parseInt(price[1], 10);
    var cap = 'filter.v.price.lte=' + amount;
    if (best) {
      return {
        label: fill(strings.priceIntentIn, { label: best.label, amount: formatWhole(amount) }),
        url: best.url + '?' + cap,
        size: best.size,
        exact: best.exact,
      };
    }
    var all = (JCO.routes && JCO.routes.allProducts) || '/collections/all';
    return { label: fill(strings.priceIntent, { amount: formatWhole(amount) }), url: all + '?' + cap, size: 0, exact: !rest };
  };

  /* ------------------------------------------------------------------ *
   * Focus handling shared by drawers and sheets
   * ------------------------------------------------------------------ */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    var items = $$(FOCUSABLE, container).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  var openLayers = [];

  function lockPage(on) {
    body.classList.toggle('drawer-open', on);
    body.classList.toggle('mbar-suppressed', on);
    root.style.overflow = on ? 'hidden' : '';
  }

  /** Opens a drawer or sheet: shows it, locks the page, traps focus, closes on Escape. */
  JCO.openLayer = function (layer, opts) {
    opts = opts || {};
    if (!layer || layer.classList.contains('is-open')) return;
    var returnFocus = document.activeElement;
    var onKey = function (event) {
      if (event.key === 'Escape') { JCO.closeLayer(layer); return; }
      trapFocus(layer, event);
    };
    layer.__jco = { returnFocus: returnFocus, onKey: onKey, scrim: opts.scrim, trigger: opts.trigger, onClose: opts.onClose };
    if (layer.hasAttribute('hidden')) layer.removeAttribute('hidden');
    // Next frame, so the transition runs from the closed state.
    requestAnimationFrame(function () {
      layer.classList.add('is-open');
      if (opts.scrim) opts.scrim.classList.add('is-open');
    });
    if (opts.trigger) opts.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', onKey);
    openLayers.push(layer);
    lockPage(true);
    var focusTarget = opts.focus || layer;
    setTimeout(function () { focusTarget.focus({ preventScroll: true }); }, 60);
  };

  JCO.closeLayer = function (layer) {
    if (!layer || !layer.classList.contains('is-open')) return;
    var state = layer.__jco || {};
    layer.classList.remove('is-open');
    if (state.scrim) state.scrim.classList.remove('is-open');
    if (state.trigger) state.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', state.onKey);
    openLayers = openLayers.filter(function (l) { return l !== layer; });
    if (!openLayers.length) lockPage(false);
    if (state.onClose) state.onClose();
    if (state.returnFocus && state.returnFocus.focus) state.returnFocus.focus({ preventScroll: true });
    if (location.hash === '#' + layer.id) history.replaceState(null, '', location.pathname + location.search);
  };

  /* ------------------------------------------------------------------ *
   * Toast
   * ------------------------------------------------------------------ */
  var toastTimer;
  JCO.toast = function (message) {
    var toast = $('[data-toast]');
    if (!toast) return;
    $('[data-toast-message]', toast).textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.classList.remove('is-visible'); }, 2600);
  };

  /* ------------------------------------------------------------------ *
   * Section reveals — fade in as sections enter, staggered by --reveal-delay.
   * JCO.reveal(elements) takes ones added later (more products on a collection page).
   * ------------------------------------------------------------------ */
  var revealer = null;
  JCO.reveal = function (items) {
    items = Array.prototype.slice.call(items || []);
    if (!revealer) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    items.forEach(function (el) { revealer.observe(el); });
  };

  function initReveals() {
    if (root.classList.contains('anim') && 'IntersectionObserver' in window) {
      revealer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });
    }
    JCO.reveal($$('[data-reveal]'));
  }

  /* ------------------------------------------------------------------ *
   * Header: transparent over the hero on tablets and desktops, measured height
   * ------------------------------------------------------------------ */
  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;

    var setHeight = function () {
      var bar = $('.site-header__bar', header);
      if (bar) root.style.setProperty('--site-header-height', bar.offsetHeight + 'px');
    };
    setHeight();
    window.addEventListener('resize', setHeight);

    if (!header.classList.contains('site-header--clear-capable')) return;
    var firstSection = $('main > .shopify-section');
    var hero = firstSection && $('[data-hero]', firstSection);
    if (!hero) return;

    var ticking = false;
    var update = function () {
      ticking = false;
      // Phones show the hero's words under its photo, so the header stays solid there.
      if (phone.matches) { header.classList.remove('is-clear'); return; }
      var heroBottom = hero.getBoundingClientRect().bottom;
      var headerHeight = header.offsetHeight;
      var menuOpen = header.querySelector('.mega.is-open');
      header.classList.toggle('is-clear', heroBottom > headerHeight + 8 && !menuOpen);
    };
    header.__updateClear = update;
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ------------------------------------------------------------------ *
   * Category dropdowns — hover on a mouse, a first tap on touch, focus on the keyboard.
   * The category is a link: a mouse hovers to open and clicks through; a touch screen opens
   * the dropdown on the first tap and follows the link on the next; the keyboard opens it on
   * focus and leaves it on Tab away or Escape.
   * ------------------------------------------------------------------ */
  function initMegaMenu() {
    var header = $('[data-header]');
    $$('[data-mega-item]').forEach(function (item) {
      var trigger = $('[data-mega-trigger]', item);
      var panel = $('[data-mega]', item);
      if (!trigger || !panel) return;
      var closeTimer;

      /* Centre the card under its category, kept inside the header's width. */
      var place = function () {
        if (!header) return;
        var hb = header.getBoundingClientRect();
        var tb = trigger.getBoundingClientRect();
        var half = panel.offsetWidth / 2;
        var margin = 16;
        var centre = tb.left - hb.left + tb.width / 2;
        if (hb.width - 2 * margin > 2 * half) centre = Math.min(Math.max(centre, margin + half), hb.width - margin - half);
        panel.style.setProperty('--mega-left', centre + 'px');
      };

      var open = function () {
        clearTimeout(closeTimer);
        place();
        $$('[data-mega].is-open').forEach(function (other) {
          if (other !== panel) {
            other.classList.remove('is-open');
            var t = $('[data-mega-trigger]', other.parentElement);
            if (t) t.setAttribute('aria-expanded', 'false');
          }
        });
        panel.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        if (header && header.__updateClear) header.__updateClear();
      };
      var close = function () {
        clearTimeout(closeTimer);
        panel.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        if (header && header.__updateClear) header.__updateClear();
      };
      var closeSoon = function () {
        clearTimeout(closeTimer);
        closeTimer = setTimeout(close, 160);
      };

      trigger.addEventListener('click', function (event) {
        if (finePointer.matches || panel.classList.contains('is-open')) return;
        event.preventDefault();
        open();
      });
      trigger.addEventListener('focus', function () {
        if (trigger.matches(':focus-visible')) open();
      });
      item.addEventListener('mouseenter', function () { if (finePointer.matches) open(); });
      item.addEventListener('mouseleave', function () { if (finePointer.matches) closeSoon(); });
      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) close();
      });
      item.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && panel.classList.contains('is-open')) {
          close();
          trigger.focus();
        }
      });
      item.__closeMega = close;
    });

    // Escape, or a tap outside, closes an open dropdown wherever focus happens to be.
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      $$('[data-mega-item]').forEach(function (item) {
        if (item.__closeMega && $('[data-mega].is-open', item)) item.__closeMega();
      });
    });
    document.addEventListener('click', function (event) {
      $$('[data-mega-item]').forEach(function (item) {
        if (item.__closeMega && !item.contains(event.target) && $('[data-mega].is-open', item)) item.__closeMega();
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Phone menu drawer + accordion rows. The header's menu button and the phone dock's
   * menu slot open the same drawer.
   * ------------------------------------------------------------------ */
  function initMenuDrawer() {
    var drawer = $('[data-menu-drawer]');
    if (!drawer) return;
    var scrim = $('[data-menu-scrim]');

    $$('[data-menu-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        JCO.openLayer(drawer, { scrim: scrim, trigger: trigger });
      });
    });
    $$('[data-menu-close]', drawer).forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        JCO.closeLayer(drawer);
      });
    });
    if (scrim) scrim.addEventListener('click', function () { JCO.closeLayer(drawer); });
    // Opened by the no-JS fallback (#menu-drawer) before this script arrived.
    if (location.hash === '#menu-drawer') {
      history.replaceState(null, '', location.pathname + location.search);
    }

    $$('[data-accordion]', drawer).forEach(function (button) {
      var panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;
      panel.hidden = true;
      button.addEventListener('click', function () {
        var expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        if (reducedMotion.matches) { panel.hidden = expanded; return; }
        if (expanded) {
          panel.style.height = panel.scrollHeight + 'px';
          panel.classList.add('is-animating');
          requestAnimationFrame(function () { panel.style.height = '0px'; });
          setTimeout(function () { panel.hidden = true; panel.classList.remove('is-animating'); panel.style.height = ''; }, 330);
        } else {
          panel.hidden = false;
          panel.style.height = '0px';
          panel.classList.add('is-animating');
          requestAnimationFrame(function () { panel.style.height = panel.scrollHeight + 'px'; });
          setTimeout(function () { panel.classList.remove('is-animating'); panel.style.height = ''; }, 330);
        }
      });
    });

    desktop.addEventListener('change', function (event) {
      if (event.matches) JCO.closeLayer(drawer);
    });
  }

  /* ------------------------------------------------------------------ *
   * Hero slideshow. Slides cross-fade on their own after data-interval ms while autoplay is
   * on, and only the pause button stops them: a mouse resting on the hero (which fills most
   * of a desktop screen) or a device's reduce-motion setting must not. They also wait,
   * unseen, while keyboard focus is inside, the hero is off screen or the tab is hidden.
   * Dots, arrows, swipes and the arrow keys move them by hand. Under reduced motion the
   * slides swap without the fade (theme.css). Inactive slides are inert.
   * ------------------------------------------------------------------ */
  function initHero(hero) {
    if (!hero || hero.__hero) return;
    var slides = $$('[data-hero-slide]', hero);
    if (slides.length < 2) return;
    hero.__hero = true;
    var track = $('[data-hero-track]', hero);
    var dots = $$('[data-hero-dot]', hero);
    var pauseButton = $('[data-hero-pause]', hero);
    var interval = Math.max(3000, parseInt(hero.getAttribute('data-interval'), 10) || 5000);
    var stopped = hero.getAttribute('data-autoplay') !== 'true';
    var current = 0;
    var timer = null;
    var leaveTimer = null;
    var focused = false;
    var onScreen = true;

    var setPauseButton = function () {
      if (!pauseButton) return;
      pauseButton.classList.toggle('is-paused', stopped);
      pauseButton.setAttribute('aria-label', pauseButton.getAttribute(stopped ? 'data-label-play' : 'data-label-pause'));
    };

    var schedule = function () {
      clearTimeout(timer);
      track.setAttribute('aria-live', stopped ? 'polite' : 'off');
      if (stopped || focused || !onScreen || document.hidden) return;
      timer = setTimeout(function () { show(current + 1); }, interval);
    };

    var show = function (index) {
      var next = (index + slides.length) % slides.length;
      if (next === current) { schedule(); return; }
      var from = slides[current];
      var to = slides[next];
      clearTimeout(leaveTimer);
      slides.forEach(function (slide) { if (slide !== from) slide.classList.remove('is-leaving'); });
      hero.classList.toggle('is-new-copy', from.getAttribute('data-copy') !== to.getAttribute('data-copy'));
      from.classList.remove('is-active');
      from.classList.add('is-leaving');
      from.setAttribute('aria-hidden', 'true');
      from.inert = true;
      to.classList.add('is-active');
      to.removeAttribute('aria-hidden');
      to.inert = false;
      leaveTimer = setTimeout(function () { from.classList.remove('is-leaving'); }, 850);
      dots.forEach(function (dot, i) {
        if (i === next) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      current = next;
      schedule();
    };

    hero.classList.add('is-ready');
    setPauseButton();

    dots.forEach(function (dot, i) { dot.addEventListener('click', function () { show(i); }); });
    var prev = $('[data-hero-prev]', hero);
    var nextButton = $('[data-hero-next]', hero);
    if (prev) prev.addEventListener('click', function () { show(current - 1); });
    if (nextButton) nextButton.addEventListener('click', function () { show(current + 1); });
    if (pauseButton) {
      pauseButton.addEventListener('click', function () {
        stopped = !stopped;
        setPauseButton();
        if (!stopped) focused = false;
        schedule();
      });
    }
    hero.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!event.target.closest('.hero__controls')) return;
      event.preventDefault();
      show(current + (event.key === 'ArrowRight' ? 1 : -1));
    });

    // Focus holds the slide only when it came from the keyboard (someone working the
    // controls or reading a slide's buttons); a clicked or tapped dot is no reason to stop.
    hero.addEventListener('focusin', function (event) {
      var keyboard = true;
      try { keyboard = event.target.matches(':focus-visible'); } catch (err) { /* older browsers: treat as keyboard */ }
      if (!keyboard) return;
      focused = true;
      schedule();
    });
    hero.addEventListener('focusout', function (event) {
      if (event.relatedTarget && hero.contains(event.relatedTarget)) return;
      focused = false;
      schedule();
    });
    document.addEventListener('visibilitychange', schedule);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        schedule();
      }, { threshold: 0.25 }).observe(hero);
    }

    // Swipe: a mostly sideways drag of 40px moves one slide; vertical drags scroll the page.
    var startX = null;
    var startY = 0;
    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse') return;
      startX = event.clientX;
      startY = event.clientY;
    });
    track.addEventListener('pointerup', function (event) {
      if (startX === null) return;
      var dx = event.clientX - startX;
      var dy = event.clientY - startY;
      startX = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) show(current + (dx < 0 ? 1 : -1));
    });
    track.addEventListener('pointercancel', function () { startX = null; });

    // Theme editor: selecting a slide shows it and holds it there.
    hero.addEventListener('shopify:block:select', function (event) {
      var index = slides.indexOf(event.target.closest('[data-hero-slide]'));
      if (index < 0) return;
      focused = true;
      show(index);
    });
    hero.addEventListener('shopify:block:deselect', function () { focused = false; schedule(); });

    schedule();
  }

  /* ------------------------------------------------------------------ *
   * Search sheet: popular searches, then suggestions as you type — the search intent
   * first when the words carry one, then search terms, collections, products and pages,
   * a combobox the arrow keys move through. A search that is exactly an intent's word
   * goes straight to its link.
   * ------------------------------------------------------------------ */
  function initSearch() {
    var sheet = $('[data-search-sheet]');
    if (!sheet) return;
    var input = $('[data-search-input]', sheet);
    var suggestions = $('[data-search-suggestions]', sheet);
    var popular = $('[data-search-popular]', sheet);
    var clear = $('[data-search-clear]', sheet);
    var status = $('[data-search-status]', sheet);
    var routes = JCO.routes || {};
    var controller;
    var timer;
    var active = -1;

    var open = function (event, trigger) {
      if (event) event.preventDefault();
      JCO.openLayer(sheet, {
        trigger: trigger,
        focus: input,
        // The sheet fades out, then leaves the page entirely so it can't catch clicks.
        onClose: function () {
          setTimeout(function () { if (!sheet.classList.contains('is-open')) sheet.hidden = true; }, 300);
        },
      });
    };
    $$('[data-search-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function (event) { open(event, trigger); });
    });
    $$('[data-search-close]', sheet).forEach(function (btn) {
      btn.addEventListener('click', function () { JCO.closeLayer(sheet); });
    });
    sheet.addEventListener('click', function (event) {
      if (event.target === sheet) JCO.closeLayer(sheet);
    });

    var items = function () { return $$('[data-suggest-item]', suggestions); };
    var highlight = function (index) {
      var list = items();
      list.forEach(function (el, i) {
        el.classList.toggle('is-active', i === index);
        el.setAttribute('aria-selected', String(i === index));
      });
      if (list[index]) {
        list[index].scrollIntoView({ block: 'nearest' });
        input.setAttribute('aria-activedescendant', list[index].id);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
      active = index;
    };

    /* The intent behind the words, as the first option: "Shop Totes" for "tote". */
    var intentRow = function (intent) {
      var row = document.createElement('a');
      row.className = 'mbar-search__row mbar-search__row--intent';
      row.href = intent.url;
      row.id = 'predictive-intent';
      row.setAttribute('role', 'option');
      row.setAttribute('aria-selected', 'false');
      row.setAttribute('data-suggest-item', '');
      var name = document.createElement('span');
      name.className = 'mbar-search__name';
      var parts = String(strings.searchIntent || 'Shop {{ label }}').split(/\{\{\s*label\s*\}\}/);
      var label = document.createElement('b');
      label.textContent = intent.label;
      name.appendChild(document.createTextNode(parts[0] || ''));
      name.appendChild(label);
      name.appendChild(document.createTextNode(parts[1] || ''));
      row.appendChild(name);
      row.insertAdjacentHTML('beforeend', '<svg class="icon" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><path d="M3 8h10M9 4l4 4-4 4"/></svg>');
      return row;
    };

    /** Puts suggestions in place; the popular searches show whenever there are none. */
    var show = function (html) {
      suggestions.innerHTML = html || '';
      var intent = input.value.trim() ? JCO.matchIntent(input.value) : null;
      var listbox = $('[role="listbox"]', suggestions);
      var row = null;
      if (intent) {
        if (!listbox) {
          listbox = document.createElement('div');
          listbox.className = 'mbar-search__listbox';
          listbox.id = 'predictive-search-listbox';
          listbox.setAttribute('role', 'listbox');
          listbox.setAttribute('aria-label', strings.searchSuggestions || '');
          suggestions.insertBefore(listbox, suggestions.firstChild);
        }
        row = intentRow(intent);
        listbox.insertBefore(row, listbox.firstChild);
        // "No products match" would contradict the way in offered just above it.
        var note = $('.mbar-search__note', suggestions);
        if (note) note.remove();
      }
      input.setAttribute('aria-expanded', String(Boolean(listbox)));
      if (popular) popular.hidden = Boolean(listbox);
      var summary = $('[data-predictive-count], .mbar-search__note', suggestions) || row;
      if (status) status.textContent = summary ? summary.textContent.trim() : '';
      highlight(-1);
      if (html) JCO.pricesChanged(suggestions);
    };

    if (input.form) {
      input.form.addEventListener('submit', function (event) {
        var intent = JCO.matchIntent(input.value);
        if (intent && intent.exact) {
          event.preventDefault();
          window.location.href = intent.url;
        }
      });
    }

    var fetchSuggestions = function (terms) {
      if (controller) controller.abort();
      if (!terms) { show(''); return; }
      controller = new AbortController();
      // Search terms, collections, products and pages, up to six of each.
      var url = (routes.predictiveSearch || '/search/suggest')
        + '?q=' + encodeURIComponent(terms)
        + '&resources%5Btype%5D=query,collection,product,page'
        + '&resources%5Blimit%5D=6&resources%5Blimit_scope%5D=each'
        + '&resources%5Boptions%5D%5Bunavailable_products%5D=last'
        + '&section_id=predictive-search';
      fetch(url, { signal: controller.signal })
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var section = doc.querySelector('.shopify-section') || doc.body;
          show(section.innerHTML.trim());
        })
        .catch(function (err) { if (err.name !== 'AbortError') show(''); });
    };

    input.addEventListener('input', function () {
      clearTimeout(timer);
      var terms = input.value.trim();
      if (clear) clear.hidden = !input.value;
      timer = setTimeout(function () { fetchSuggestions(terms); }, 180);
    });
    if (clear) {
      clear.addEventListener('click', function () {
        clearTimeout(timer);
        if (controller) controller.abort();
        input.value = '';
        clear.hidden = true;
        show('');
        input.focus();
      });
    }
    input.addEventListener('keydown', function (event) {
      var list = items();
      if (!list.length) return;
      if (event.key === 'ArrowDown') { event.preventDefault(); highlight(Math.min(active + 1, list.length - 1)); }
      else if (event.key === 'ArrowUp') { event.preventDefault(); highlight(Math.max(active - 1, 0)); }
      else if (event.key === 'Enter' && active > -1 && list[active]) { event.preventDefault(); list[active].click(); }
    });
  }

  /* ------------------------------------------------------------------ *
   * Product-card tilt — a slight 3D lean toward the pointer
   * ------------------------------------------------------------------ */
  function initTilt() {
    if (!JCO.tilt || reducedMotion.matches || !finePointer.matches) return;
    document.addEventListener('pointermove', function (event) {
      var card = event.target.closest && event.target.closest('[data-tilt]');
      if (!card) return;
      var rect = card.getBoundingClientRect();
      var x = (event.clientX - rect.left) / rect.width - 0.5;
      var y = (event.clientY - rect.top) / rect.height - 0.5;
      card.style.setProperty('--tilt-x', (-y * 4).toFixed(2) + 'deg');
      card.style.setProperty('--tilt-y', (x * 4).toFixed(2) + 'deg');
    }, { passive: true });
    document.addEventListener('pointerout', function (event) {
      var card = event.target.closest && event.target.closest('[data-tilt]');
      if (card && !card.contains(event.relatedTarget)) {
        card.style.setProperty('--tilt-x', '0deg');
        card.style.setProperty('--tilt-y', '0deg');
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Quick view — a card's quick-view control opens the product in a dialog. Shopify
   * renders sections/quick-view.liquid for that product (Section Rendering API) and
   * product.js wires its form; a failed load falls back to the trigger's own link, the
   * product page.
   *
   * Quick add is the same dialog, compact: a card's Add to cart or Buy now on a product
   * with options, and the phone dock's Add to cart, open it to choose them.
   * ------------------------------------------------------------------ */
  function initQuickView() {
    var layer = $('[data-quick-view]');
    if (!layer) return;
    var content = $('[data-quick-view-body]', layer);
    var cache = {};
    var TRIGGER = '[data-quick-view-open], [data-quick-add]';

    var load = function (url) {
      if (!cache[url]) {
        cache[url] = fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=quick-view')
          .then(function (res) {
            if (!res.ok) throw new Error('Quick view ' + res.status);
            return res.text();
          })
          .catch(function (err) { delete cache[url]; throw err; });
      }
      return cache[url];
    };

    /** Opens the product at `url` (a product URL, optionally with ?variant=). */
    JCO.openQuickView = function (url, opts) {
      opts = opts || {};
      var trigger = opts.trigger;
      if (trigger && trigger.getAttribute('aria-busy') === 'true') return;
      if (trigger) trigger.setAttribute('aria-busy', 'true');
      load(url)
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var section = doc.querySelector('.shopify-section') || doc.body;
          if (!section.querySelector('[data-product-json]')) throw new Error('Quick view is empty');
          content.innerHTML = section.innerHTML;
          content.scrollTop = 0;
          layer.classList.toggle('quick-view--add', opts.mode === 'add');
          JCO.openLayer(layer, {
            trigger: trigger,
            // Fades out, then leaves the page so it can't catch clicks.
            onClose: function () {
              setTimeout(function () { if (!layer.classList.contains('is-open')) layer.hidden = true; }, 320);
            },
          });
          // Wired once shown, so the gallery can measure itself.
          if (JCO.initProduct) JCO.initProduct(content, { quickView: true });
          JCO.pricesChanged(content);
        })
        .catch(function () { window.location.href = url; })
        .finally(function () { if (trigger) trigger.removeAttribute('aria-busy'); });
    };

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest && event.target.closest(TRIGGER);
      // A modified click still opens the product page, in a new tab or window.
      if (!trigger || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      JCO.openQuickView(trigger.getAttribute('href'), {
        trigger: trigger,
        mode: trigger.hasAttribute('data-quick-add') ? 'add' : 'view',
      });
    });
    // Start loading as the pointer reaches a trigger, so the dialog opens straight away.
    document.addEventListener('pointerover', function (event) {
      var trigger = event.target.closest && event.target.closest(TRIGGER);
      if (trigger) load(trigger.getAttribute('href')).catch(function () {});
    });
    $$('[data-quick-view-close]', layer).forEach(function (btn) {
      btn.addEventListener('click', function () { JCO.closeLayer(layer); });
    });
    layer.addEventListener('click', function (event) {
      if (event.target === layer) JCO.closeLayer(layer);
    });
  }

  /* ------------------------------------------------------------------ *
   * Product-card swatches — a colour shows its own photo, and carries into the card's
   * links: the product page and the quick view open on that colour
   * ------------------------------------------------------------------ */
  function initCardSwatches() {
    document.addEventListener('click', function (event) {
      var swatch = event.target.closest && event.target.closest('[data-swatch]');
      var card = swatch && swatch.closest('[data-product-card]');
      if (!card) return;
      event.preventDefault();
      $$('[data-swatch]', card).forEach(function (s) { s.setAttribute('aria-pressed', String(s === swatch)); });
      var image = $('.product-card__image--primary', card);
      if (image && swatch.getAttribute('data-src')) {
        image.srcset = swatch.getAttribute('data-srcset') || '';
        image.src = swatch.getAttribute('data-src');
      }
      card.classList.add('is-swatched');
      var base = card.getAttribute('data-product-url');
      var url = base + (base.indexOf('?') > -1 ? '&' : '?') + 'variant=' + swatch.getAttribute('data-variant-id');
      $$('[data-product-link]', card).forEach(function (link) { link.setAttribute('href', url); });
    });
  }

  /* ------------------------------------------------------------------ *
   * Detail tabs — every panel is in the HTML; only the inactive ones are hidden
   * ------------------------------------------------------------------ */
  function initTabs() {
    $$('[data-tabs]').forEach(function (group) {
      var tabs = $$('[data-tab]', group);
      if (!tabs.length) return;
      var select = function (tab, focus) {
        tabs.forEach(function (t) {
          var selected = t === tab;
          t.setAttribute('aria-selected', String(selected));
          t.tabIndex = selected ? 0 : -1;
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });
        if (focus) tab.focus();
      };
      tabs.forEach(function (tab, index) {
        tab.addEventListener('click', function () { select(tab); });
        tab.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
          if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
          if (event.key === 'Home') next = tabs[0];
          if (event.key === 'End') next = tabs[tabs.length - 1];
          if (next) { event.preventDefault(); select(next, true); }
        });
      });
      select(tabs[0]);
    });
  }

  /* ------------------------------------------------------------------ *
   * Size-guide dialog
   * ------------------------------------------------------------------ */
  function initSizeGuide() {
    var dialog = $('[data-size-guide]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    $$('[data-size-guide-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        dialog.showModal();
      });
    });
    $$('[data-size-guide-close]', dialog).forEach(function (btn) {
      btn.addEventListener('click', function () { dialog.close(); });
    });
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });
  }

  /* ------------------------------------------------------------------ *
   * Phone dock — always in view. On a product page its Add to cart opens up once the
   * page's own button is scrolled past. A product with options opens the quick add on
   * the choice made on the page, to confirm or change it; one without adds straight away.
   * ------------------------------------------------------------------ */
  function initMobileBar() {
    var bar = $('[data-mbar]');
    if (!bar) return;
    var atc = $('[data-mbar-atc]', bar);
    var section = $('[data-product-section]');
    var pageButton = section && $('[data-add-button]', section);
    if (!atc || !pageButton || !('IntersectionObserver' in window)) return;
    // Watch both of the page's buttons, and count them gone once they pass under the
    // sticky header rather than off the top of the screen.
    var watched = pageButton.closest('.product-template__actions') || pageButton;
    var headerBar = $('[data-header] .site-header__bar');
    var headerHeight = headerBar ? headerBar.offsetHeight : 0;
    new IntersectionObserver(function (entries) {
      var entry = entries[0];
      var top = entry.rootBounds ? entry.rootBounds.top : headerHeight;
      var above = !entry.isIntersecting && entry.boundingClientRect.top < top;
      bar.setAttribute('data-atc', above ? 'on' : 'off');
      atc.tabIndex = above ? 0 : -1;
      if (above) atc.removeAttribute('aria-hidden'); else atc.setAttribute('aria-hidden', 'true');
    }, { rootMargin: '-' + headerHeight + 'px 0px 0px 0px' }).observe(watched);
    atc.addEventListener('click', function () {
      var picked = pageButton.form && $('[data-variant-select]', pageButton.form);
      if ($('[data-option]', section) && picked && JCO.openQuickView) {
        JCO.openQuickView(location.pathname + '?variant=' + picked.value, { mode: 'add', trigger: atc });
        return;
      }
      if (!pageButton.disabled) pageButton.click();
    });
  }

  /* ------------------------------------------------------------------ *
   * Quantity steppers (product form; cart lines are handled by cart.js)
   * ------------------------------------------------------------------ */
  function initSteppers() {
    document.addEventListener('click', function (event) {
      var step = event.target.closest && event.target.closest('[data-qty-step]');
      if (!step || step.closest('[data-cart-line]')) return;
      var wrap = step.closest('.product-template__qty');
      var input = wrap && $('[data-qty-input]', wrap);
      if (!input) return;
      var min = parseInt(input.min || '1', 10);
      var next = Math.max(min, (parseInt(input.value, 10) || min) + parseInt(step.getAttribute('data-qty-step'), 10));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ------------------------------------------------------------------ *
   * Collection sort — apply on change
   * ------------------------------------------------------------------ */
  function initSort() {
    $$('[data-sort-select]').forEach(function (select) {
      select.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Instagram feed — the account's latest posts, when the section carries an access
   * token (Instagram API with Instagram Login) or a feed endpoint answering with the same
   * JSON. Cached for an hour per visit; the section's own photos stay until the posts
   * arrive, and a section with nothing else to show steps aside if they never do.
   * ------------------------------------------------------------------ */
  function initInstagram(scope) {
    $$('[data-instagram]', scope).forEach(function (grid) {
      if (grid.__instagram) return;
      grid.__instagram = true;
      var token = grid.getAttribute('data-instagram-token') || '';
      var feed = grid.getAttribute('data-instagram-feed') || '';
      var limit = parseInt(grid.getAttribute('data-instagram-count') || '6', 10) || 6;
      var label = grid.getAttribute('data-instagram-label') || '';
      if (!token && !feed) return;
      var url = feed || ('https://graph.instagram.com/me/media?fields=id,caption,media_type,media_url,thumbnail_url,permalink&limit=' + limit + '&access_token=' + encodeURIComponent(token));
      var cacheKey = 'jco-instagram:' + url;
      var section = grid.closest('.shopify-section') || grid.closest('section');

      var render = function (posts) {
        posts = (posts || []).filter(function (p) { return p && (p.media_url || p.thumbnail_url) && p.permalink; }).slice(0, limit);
        if (!posts.length) return false;
        var frag = document.createDocumentFragment();
        posts.forEach(function (post) {
          var li = document.createElement('li');
          var link = document.createElement('a');
          link.className = 'instagram__tile';
          link.href = post.permalink;
          link.target = '_blank';
          link.rel = 'noopener';
          var img = document.createElement('img');
          img.src = post.media_type === 'VIDEO' ? (post.thumbnail_url || post.media_url) : post.media_url;
          img.alt = String(post.caption || '').replace(/\s+/g, ' ').trim().slice(0, 120);
          img.loading = 'lazy';
          img.width = 600;
          img.height = 600;
          link.appendChild(img);
          if (label) {
            var caption = document.createElement('span');
            caption.className = 'instagram__label';
            caption.textContent = label;
            link.appendChild(caption);
          }
          li.appendChild(link);
          frag.appendChild(li);
        });
        grid.innerHTML = '';
        grid.appendChild(frag);
        return true;
      };
      var giveUp = function () {
        if (grid.hasAttribute('data-instagram-empty') && section) section.hidden = true;
      };

      var cached = null;
      try { cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null'); } catch (err) { cached = null; }
      if (cached && cached.at && Date.now() - cached.at < 3600000 && render(cached.posts)) return;

      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (res) {
          if (!res.ok) throw new Error('Instagram ' + res.status);
          return res.json();
        })
        .then(function (json) {
          var posts = json.data || json.posts || [];
          if (!render(posts)) { giveUp(); return; }
          try { sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), posts: posts.slice(0, 12) })); } catch (err) { /* storage may be unavailable */ }
        })
        .catch(giveUp);
    });
  }

  function init() {
    initReveals();
    $$('[data-hero]').forEach(initHero);
    initHeader();
    initMegaMenu();
    initMenuDrawer();
    initSearch();
    initTilt();
    initQuickView();
    initCardSwatches();
    initTabs();
    initSizeGuide();
    initMobileBar();
    initSteppers();
    initSort();
    initInstagram(document);
    // Theme editor: a section added or changed in place starts its own behaviour.
    document.addEventListener('shopify:section:load', function (event) {
      $$('[data-hero]', event.target).forEach(initHero);
      initInstagram(event.target);
      JCO.reveal($$('[data-reveal]', event.target));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
